import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Send,
  CalendarIcon,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  User,
  Users,
  CreditCard,
  Search,
  AlertCircle,
  ArrowLeft,
  Filter,
  Clock,
} from "lucide-react";
import { cn } from "../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEDPage = ({ user, settings }) => {
  const [clients, setClients] = useState([]);
  const [clientsWithStatus, setClientsWithStatus] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [conditions, setConditions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [transmissionDate, setTransmissionDate] = useState(new Date());
  const [transmittingFor, setTransmittingFor] = useState("principal");
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "ready", "waiting", "completed"

  useEffect(() => {
    fetchClientsWithStatus();
  }, []);

  // Fetch all clients with their TED status in a single optimized request
  const fetchClientsWithStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/ted/batch-status`, {
        withCredentials: true,
      });
      setClientsWithStatus(response.data);
      setClients(response.data); // Keep clients array for backward compatibility
    } catch (error) {
      console.error("Error fetching clients with status:", error);
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let result = [...clientsWithStatus];
    
    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          c.nom.toLowerCase().includes(search) ||
          c.prenom.toLowerCase().includes(search) ||
          c.code_client?.toLowerCase().includes(search) ||
          c.ville.toLowerCase().includes(search)
      );
    }
    
    // Filter by status - data is now directly in the client object from batch-status
    if (statusFilter !== "all" && result.length > 0) {
      result = result.filter((c) => {
        const isSpouseNonResident = c.spouse_non_resident || c.conjoint?.residence === "Non Résident";
        const principalDone = c.principal_transmitted;
        const spouseDone = !c.has_spouse || isSpouseNonResident || c.spouse_transmitted;
        const allTransmitted = principalDone && spouseDone;
        
        switch (statusFilter) {
          case "ready":
            return c.ready_for_transmission && !allTransmitted;
          case "waiting":
            return !c.ready_for_transmission;
          case "completed":
            return allTransmitted;
          default:
            return true;
        }
      });
    }
    
    // Sort by updated_at descending (most recent first) - already sorted by backend
    setFilteredClients(result);
  }, [clientsWithStatus, searchTerm, statusFilter]);

  const handleClientSelect = async (client) => {
    setSelectedClient(client);
    setSearchTerm("");
    setChecking(true);
    
    try {
      const response = await axios.get(`${API}/ted/check/${client.client_id}`, {
        withCredentials: true,
      });
      setConditions(response.data);
    } catch (error) {
      console.error("Error checking conditions:", error);
      toast.error("Erreur lors de la vérification");
      setConditions(null);
    } finally {
      setChecking(false);
    }
  };

  const handleBackToList = () => {
    setSelectedClient(null);
    setConditions(null);
  };

  const openTransmissionDialog = (forPerson) => {
    setTransmittingFor(forPerson);
    setTransmissionDate(new Date());
    setDialogOpen(true);
  };

  const handleTransmit = async () => {
    if (!selectedClient || !transmissionDate) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/ted`,
        {
          client_id: selectedClient.client_id,
          is_principal: transmittingFor === "principal",
          date_transmission: transmissionDate.toISOString(),
        },
        { withCredentials: true }
      );
      toast.success("Transmission TED effectuée avec succès");
      setDialogOpen(false);
      // Refresh conditions and client list
      handleClientSelect(selectedClient);
      fetchClientsWithStatus(); // Refresh the full list in background
    } catch (error) {
      console.error("Error transmitting:", error);
      toast.error(
        error.response?.data?.detail || "Erreur lors de la transmission"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Use spouse_non_resident from API conditions, fallback to client data
  const isSpouseNonResident = conditions?.spouse_non_resident || selectedClient?.conjoint?.residence === "Non Résident";

  const StatusIndicator = ({ status, label }) => (
    <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
      <span className={`status-indicator ${status ? "green" : "red"}`} />
      <div className="flex-1">
        <p className="font-medium text-foreground">{label}</p>
      </div>
      {status ? (
        <CheckCircle2 className="h-5 w-5 text-green-600" />
      ) : (
        <XCircle className="h-5 w-5 text-red-600" />
      )}
    </div>
  );

  if (loading) {
    return (
      <Layout user={user} settings={settings}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} settings={settings}>
      <div className="space-y-6" data-testid="ted-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Transmission TED
          </h1>
          <p className="text-muted-foreground mt-1">
            Vérifier les conditions et effectuer la transmission électronique
          </p>
        </div>

        {/* Conditional Display: Client List OR Full Screen TED Form */}
        {!selectedClient ? (
          /* Client Selection - Full Screen List */
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Sélection du client</CardTitle>
              <CardDescription>
                Cliquez sur un client pour vérifier les conditions de transmission
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Search and Filter */}
              <div className="space-y-4 mb-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher par nom, prénom, code client, ville..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-client-input"
                  />
                </div>
                
                {/* Quick Filters */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={statusFilter === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStatusFilter("all")}
                    data-testid="filter-all"
                  >
                    <Filter className="h-4 w-4 mr-1" />
                    Tous ({clientsWithStatus.length})
                  </Button>
                  <Button
                    variant={statusFilter === "ready" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStatusFilter("ready")}
                    className={statusFilter === "ready" ? "" : "border-green-300 text-green-700 hover:bg-green-50"}
                    data-testid="filter-ready"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Prêts ({clientsWithStatus.filter(c => {
                      const isSpouseNR = c.spouse_non_resident || c.conjoint?.residence === "Non Résident";
                      const principalDone = c.principal_transmitted;
                      const spouseDone = !c.has_spouse || isSpouseNR || c.spouse_transmitted;
                      return c.ready_for_transmission && !(principalDone && spouseDone);
                    }).length})
                  </Button>
                  <Button
                    variant={statusFilter === "waiting" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStatusFilter("waiting")}
                    className={statusFilter === "waiting" ? "" : "border-orange-300 text-orange-700 hover:bg-orange-50"}
                    data-testid="filter-waiting"
                  >
                    <Clock className="h-4 w-4 mr-1" />
                    En attente ({clientsWithStatus.filter(c => !c.ready_for_transmission).length})
                  </Button>
                  <Button
                    variant={statusFilter === "completed" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStatusFilter("completed")}
                    className={statusFilter === "completed" ? "" : "border-blue-300 text-blue-700 hover:bg-blue-50"}
                    data-testid="filter-completed"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Transmis ({clientsWithStatus.filter(c => {
                      const isSpouseNR = c.spouse_non_resident || c.conjoint?.residence === "Non Résident";
                      const principalDone = c.principal_transmitted;
                      const spouseDone = !c.has_spouse || isSpouseNR || c.spouse_transmitted;
                      return principalDone && spouseDone;
                    }).length})
                  </Button>
                </div>
              </div>

              {/* Client List - Full height */}
              <ScrollArea className="h-[calc(100vh-400px)] min-h-80 rounded-md border">
                <div className="p-2">
                  {filteredClients.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Aucun client trouvé</p>
                  ) : (
                    filteredClients.map((client) => {
                      // Determine client TED status for badge - data is directly in client object
                      let statusBadge = null;
                      const isSpouseNR = client.spouse_non_resident || client.conjoint?.residence === "Non Résident";
                      const principalDone = client.principal_transmitted;
                      const spouseDone = !client.has_spouse || isSpouseNR || client.spouse_transmitted;
                      const allTransmitted = principalDone && spouseDone;
                      
                      if (allTransmitted) {
                        statusBadge = (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
                            <Send className="h-3 w-3" />
                            Transmis
                          </span>
                        );
                      } else if (client.ready_for_transmission) {
                        statusBadge = (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Prêt
                          </span>
                        );
                      } else {
                        statusBadge = (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            En attente
                          </span>
                        );
                      }
                      
                      return (
                        <div
                          key={client.client_id}
                          onClick={() => handleClientSelect(client)}
                          className="p-3 rounded-md cursor-pointer transition-colors mb-1 hover:bg-muted"
                          data-testid={`client-select-${client.client_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium">
                                  {client.prenom} {client.nom}
                                </p>
                                <span className="text-xs font-mono text-muted-foreground">
                                  {client.code_client}
                                </span>
                                {client.preparation_gratuite && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                    Gratuit
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {client.ville} • {client.situation_familiale}
                              </p>
                              {/* Last activity date */}
                              <p className="text-xs text-muted-foreground/70">
                                Dernière activité: {client.updated_at 
                                  ? format(new Date(client.updated_at), "dd/MM/yyyy HH:mm", { locale: fr })
                                  : client.created_at 
                                    ? format(new Date(client.created_at), "dd/MM/yyyy HH:mm", { locale: fr })
                                    : "N/A"
                                }
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {statusBadge}
                              {client.conjoint && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                  + Conjoint(e)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        ) : (
          /* Full Screen TED Form for Selected Client */
          <div className="space-y-6">
            {/* Selected Client Header with Back Button */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleBackToList}
                      data-testid="back-to-list-button"
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" />
                      Retour à la liste
                    </Button>
                    <div>
                      <p className="text-sm text-muted-foreground">Client sélectionné:</p>
                      <p className="font-semibold text-lg">
                        {selectedClient.prenom} {selectedClient.nom}
                        <span className="ml-2 text-sm font-mono text-muted-foreground">
                          ({selectedClient.code_client})
                        </span>
                        {selectedClient.preparation_gratuite && (
                          <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                            Préparation gratuite
                          </span>
                        )}
                      </p>
                      {selectedClient.conjoint && (
                        <p className="text-sm text-blue-600 mt-1">
                          Conjoint(e): {selectedClient.conjoint.prenom} {selectedClient.conjoint.nom}
                          {isSpouseNonResident && (
                            <span className="ml-2 text-orange-600">(Non Résident)</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {selectedClient.ville} • {selectedClient.situation_familiale}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Loading State */}
            {checking && (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            )}

            {/* TED Conditions and Transmission - Full Width Grid */}
            {conditions && !checking && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Conditions Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-primary" />
                      Conditions de transmission
                    </CardTitle>
                    <CardDescription>
                      Vérification des prérequis pour {selectedClient.prenom} {selectedClient.nom}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* For gratuit clients, show specific message */}
                    {conditions.is_gratuit ? (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-3">
                        <p className="text-sm text-green-700 font-medium">
                          ✓ Préparation gratuite - Paiement non requis
                        </p>
                      </div>
                    ) : (
                      <StatusIndicator
                        status={conditions.payment_complete}
                        label={
                          <span className="flex items-center gap-2">
                            <CreditCard className="h-4 w-4" />
                            Paiement effectué en totalité
                          </span>
                        }
                      />
                    )}
                    <StatusIndicator
                      status={conditions.principal_signed}
                      label={
                        <span className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Signature requérant principal
                        </span>
                      }
                    />
                    {conditions.has_spouse && !isSpouseNonResident && (
                      <StatusIndicator
                        status={conditions.spouse_signed}
                        label={
                          <span className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            Signature conjoint(e)
                          </span>
                        }
                      />
                    )}
                    {conditions.has_spouse && isSpouseNonResident && (
                      <div className="flex items-center gap-3 p-4 rounded-lg bg-orange-50 border border-orange-200">
                        <AlertCircle className="h-5 w-5 text-orange-500" />
                        <div className="flex-1">
                          <p className="font-medium text-orange-700">
                            Signature conjoint(e)
                          </p>
                        </div>
                        <span className="text-sm font-semibold text-orange-600">
                          N/A (Non Applicable)
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Transmission Status Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Send className="h-5 w-5 text-primary" />
                      État de la transmission
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {conditions.ready_for_transmission ? (
                      <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                        <div className="flex items-center gap-2 text-green-700 font-medium">
                          <CheckCircle2 className="h-5 w-5" />
                          Prêt pour la transmission
                        </div>
                        <p className="text-sm text-green-600 mt-1">
                          Toutes les conditions sont remplies
                        </p>
                      </div>
                    ) : (
                      <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                        <div className="flex items-center gap-2 text-red-700 font-medium">
                          <XCircle className="h-5 w-5" />
                          Conditions non remplies
                        </div>
                        <p className="text-sm text-red-600 mt-1">
                          Veuillez compléter les éléments manquants
                        </p>
                      </div>
                    )}

                    {/* Principal Transmission */}
                    <div className="p-4 rounded-lg border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Requérant principal</span>
                        </div>
                        {conditions.principal_transmitted ? (
                          <span className="inline-flex items-center gap-1 text-sm text-green-600">
                            <CheckCircle2 className="h-4 w-4" />
                            Transmis
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            disabled={!conditions.ready_for_transmission}
                            onClick={() => openTransmissionDialog("principal")}
                            data-testid="transmit-principal-button"
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Transmettre
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Spouse Transmission */}
                    {conditions.has_spouse && !isSpouseNonResident && (
                      <div className="p-4 rounded-lg border">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">Conjoint(e)</span>
                          </div>
                          {conditions.spouse_transmitted ? (
                            <span className="inline-flex items-center gap-1 text-sm text-green-600">
                              <CheckCircle2 className="h-4 w-4" />
                              Transmis
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              disabled={!conditions.ready_for_transmission}
                              onClick={() => openTransmissionDialog("spouse")}
                              data-testid="transmit-spouse-button"
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Transmettre
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {conditions.has_spouse && isSpouseNonResident && (
                      <div className="p-4 rounded-lg border bg-orange-50 border-orange-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-orange-500" />
                            <span className="font-medium text-orange-700">Conjoint(e)</span>
                          </div>
                          <span className="text-sm font-semibold text-orange-600">
                            N/A (Non Applicable)
                          </span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Transmission Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmer la transmission TED</DialogTitle>
              <DialogDescription>
                Transmission pour{" "}
                {transmittingFor === "principal"
                  ? "le requérant principal"
                  : "le/la conjoint(e)"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Date de transmission *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !transmissionDate && "text-muted-foreground"
                      )}
                      data-testid="transmission-date-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {transmissionDate
                        ? format(transmissionDate, "PPP", { locale: fr })
                        : "Sélectionner la date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={transmissionDate}
                      onSelect={setTransmissionDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  <strong>Important:</strong> Cette action confirme que la
                  déclaration a été transmise électroniquement à l'ARC/Revenu
                  Québec.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={submitting}
              >
                Annuler
              </Button>
              <Button
                onClick={handleTransmit}
                disabled={submitting || !transmissionDate}
                data-testid="confirm-transmission-button"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                    Transmission...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Confirmer la transmission
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TEDPage;
